import React, { useCallback, useEffect, useState } from 'react';
import { FaStar, FaRegStar, FaTrash, FaSave, FaUsers, FaUndo } from 'react-icons/fa';
import { formatDate } from './shopConstants';

const Stars = ({ value = 0 }) => (
    <span className="rating-stars" aria-label={`${value} / 5`}>
        {[1, 2, 3, 4, 5].map((i) => (i <= Math.round(value) ? <FaStar key={i} /> : <FaRegStar key={i} />))}
    </span>
);

// One user's rating: the admin can change the stars / comment or delete it.
const RatingRow = ({ rating, actions, onChanged }) => {
    const [stars, setStars] = useState(rating.stars);
    const [comment, setComment] = useState(rating.comment || '');
    const dirty = stars !== rating.stars || comment !== (rating.comment || '');

    const save = async () => {
        if (await actions.updateRating(rating._id, { stars, comment })) onChanged();
    };
    const remove = async () => {
        if (!window.confirm('حذف هذا التقييم؟')) return;
        if (await actions.deleteRating(rating._id)) onChanged();
    };

    return (
        <tr>
            <td>
                <strong>{rating.user?.userName || 'مستخدم'}</strong>
                {rating.user?.phoneNumber ? <div className="muted small"><bdi>{rating.user.phoneNumber}</bdi></div> : null}
            </td>
            <td>
                <select value={stars} onChange={(e) => setStars(Number(e.target.value))}>
                    {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{'★'.repeat(n)} ({n})</option>)}
                </select>
            </td>
            <td>
                <input value={comment} maxLength={300} onChange={(e) => setComment(e.target.value)} placeholder="بدون تعليق" />
            </td>
            <td className="muted small">
                {formatDate(rating.updatedAt)}
                {rating.editedByAdmin ? <div className="edited-tag">عُدّل من الإدارة</div> : null}
            </td>
            <td className="row-actions">
                <button type="button" className="btn primary small" disabled={!dirty || actions.busy} onClick={save} title="حفظ"><FaSave /></button>
                <button type="button" className="btn danger-outline small" disabled={actions.busy} onClick={remove} title="حذف"><FaTrash /></button>
            </td>
        </tr>
    );
};

// Ratings of a shop in the admin shop modal: the shown rating (users' average, or a manual
// override the admin sets), followers count, and every rating with edit / delete.
const ShopRatingsSection = ({ shopId, actions }) => {
    const [data, setData] = useState(null);
    const [override, setOverride] = useState('');

    const load = useCallback(async () => {
        const json = await actions.listShopRatings(shopId);
        if (json) {
            setData(json);
            setOverride(json.rating?.override ? String(json.rating.override) : '');
        }
    }, [shopId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    if (!data) return <section className="detail-card"><h3>التقييمات</h3><div className="muted">جاري التحميل...</div></section>;

    const { rating, ratings, followersCount } = data;
    const shown = rating?.override || (rating?.count ? rating.average : 0);
    const overrideValue = Number(override);
    const overrideValid = override === '' || (overrideValue >= 1 && overrideValue <= 5);

    return (
        <section className="detail-card ratings-section">
            <h3>التقييمات والمتابعون</h3>
            <div className="ratings-summary">
                <div className="rating-big">
                    <strong>{shown ? Number(shown).toFixed(1) : '—'}</strong>
                    <Stars value={shown} />
                    <span className="muted small">
                        {rating?.count || 0} تقييم · متوسط المستخدمين {rating?.count ? Number(rating.average).toFixed(1) : '—'}
                    </span>
                    {rating?.override ? <span className="status-chip tone-info">تقييم يدوي من الإدارة</span> : null}
                </div>
                <div className="followers-box"><FaUsers /> <strong>{followersCount}</strong> متابع</div>
                <div className="override-box">
                    <label>تعيين التقييم الظاهر يدوياً (1 - 5)</label>
                    <div className="override-controls">
                        <input type="number" min="1" max="5" step="0.1" value={override} onChange={(e) => setOverride(e.target.value)} placeholder="مثال: 4.8" />
                        <button
                            type="button"
                            className="btn primary small"
                            disabled={!override || !overrideValid || actions.busy}
                            onClick={async () => { if (await actions.setRatingOverride(shopId, overrideValue)) load(); }}
                        >
                            تعيين
                        </button>
                        {rating?.override ? (
                            <button type="button" className="btn ghost small" disabled={actions.busy} onClick={async () => { if (await actions.setRatingOverride(shopId, null)) load(); }}>
                                <FaUndo /> إلغاء واستخدام المتوسط
                            </button>
                        ) : null}
                    </div>
                    {!overrideValid && <div className="reason-note">القيمة يجب أن تكون بين 1 و 5</div>}
                </div>
            </div>

            {ratings.length === 0 ? (
                <div className="muted">لا توجد تقييمات بعد.</div>
            ) : (
                <div className="ratings-table-wrap">
                    <table className="ratings-table">
                        <thead>
                            <tr><th>المستخدم</th><th>التقييم</th><th>التعليق</th><th>التاريخ</th><th /></tr>
                        </thead>
                        <tbody>
                            {ratings.map((r) => <RatingRow key={r._id} rating={r} actions={actions} onChanged={load} />)}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

export default ShopRatingsSection;
